import Vue from 'vue';
import Vuex from 'vuex';
import { isMobile } from 'Assets/js/components/size-checker';
import actions from './actions';
import getters from './getters';
import mutations from './mutations';
import marketCarts from './stores/marketCarts';

Vue.use(Vuex);

const featureFlags = window.global_theme_object?.feature_enabled || {};
const customerAccountsStatus =
  window.global_theme_object?.customer_accounts || {};
const customerAccountsEnabled =
  (featureFlags.user_login_flow === 'customer_accounts' ||
    featureFlags.user?.login_flow === 'customer_accounts') &&
  customerAccountsStatus.available === true &&
  customerAccountsStatus.enabled === true &&
  customerAccountsStatus.configured === true;

const debugEnabled =
  window.global_theme_debug && window.global_theme_debug.state;

const savedState = JSON.parse(localStorage.getItem('ossUser'));
const menuState = JSON.parse(localStorage.getItem('mdcMenus'));

const getUspState = () => {
  // The server payload is authoritative. Keep localStorage as a synchronized
  // copy only; it must never provide stale USP data to the store.
  const upstream = window?.MDC_USP;
  if (upstream && Array.isArray(upstream.data)) {
    return upstream.data;
  }

  try {
    localStorage.removeItem('mdcUSP');
  } catch (error) {
    // Ignore localStorage failures.
  }

  return null;
};

const uspState = getUspState();

const initialState = savedState
  ? { ...savedState, usp: uspState }
  : {
      loaded: false,
      menuLoaded: false,
      menus: menuState || null,
      devMode: window.global_theme_object?.dev_mode === 'true',
      cartLoaded: false,
      loadingStore: true,
      pageViewCount: 0,
      recommendedProducts: [],
      recommendedCartProducts: [],
      b2bApiCartHash: '',
      user: {
        acceptsMarketing: false,
        addresses: {
          edges: {}
        },
        date_of_birth: null,
        defaultAddress: null,
        email: null,
        firstName: null,
        gender: null,
        id: null,
        lastIncompleteCheckout: null,
        lastName: null,
        locale: null,
        orders: {
          edges: {}
        },
        token: null,
        password: null,
        phone: null,
        prefix: null,
        b2bUser: false,
        b2bUserList: [],
        b2b: {
          id: null,
          role: null,
          companyId: null
        }
      },
      b2bUser: false,
      b2b: {},
      b2bCarts: {
        global: {
          initiated: false,
          attachUserOpenInCart: false,
          giftMessage: '',
          discountCodes: [],
          products: {}
        },
        markets: [],
        carts: [],
        marketCarts
      },
      wishlist: [],
      wishlistLenght: 0,
      cart: {
        marketCarts: {
          locale: []
        }
      },
      isMobile: isMobile(),
      brandsmenu: null,
      brandmenus: null,
      usp: uspState || null
    };

const store = new Vuex.Store({
  state: initialState,
  getters,
  actions,
  mutations
});

store.subscribe(async (mutation, state) => {
  if (debugEnabled) {
    // eslint-disable-next-line no-console
    console.log('mutation', mutation);
  }

  if (mutation.type === 'logoutState') {
    localStorage.removeItem('ossUser');
    return;
  }

  const persistedState = { ...state };
  delete persistedState.usp;
  localStorage.setItem('ossUser', JSON.stringify(persistedState));

  function readCookie(name) {
    const match = document.cookie.match(
      new RegExp(
        `(?:^|; )${name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1')}=([^;]*)`
      )
    );
    return match ? decodeURIComponent(match[1]) : null;
  }

  const isAuthed = readCookie('STYXKEY_user_authed') === '1';
  const hasToken = !!readCookie('STYXKEY_user_session');

  if (isAuthed && !hasToken && !customerAccountsEnabled) {
    document.cookie =
      'STYXKEY_user_cart=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    document.cookie =
      'STYXKEY_shopify_defined_locale=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    document.cookie =
      'STYXKEY_user_session=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    document.cookie =
      'STYXKEY_user_email=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';

    const newState = { ...state };
    newState.b2bUser = false;
    newState.user = {};
    newState.b2b = {};
    newState.b2bApiCartHash = '';
    newState.cartLoaded = false;
    newState.wishlist = [];
    newState.b2bCarts = {};

    localStorage.setItem('ossUser', JSON.stringify(newState));

    window.dispatchEvent(
      new CustomEvent('updated_store', { detail: { store } })
    );

    window.dispatchEvent(
      new CustomEvent('error-toggled', {
        detail: {
          subtitle: 'User session',
          title: 'Session expired',
          content: 'Please log back in'
        }
      })
    );
  }

  if (mutation.type === 'addWishlistItem') {
    window.dispatchEvent(
      new CustomEvent('updated_store', { detail: { store } })
    );
  }

  if (
    mutation.type === 'addingContactItem' ||
    mutation.type === 'removingContactItem' ||
    mutation.type === 'addB2bMarketCheckoutUrl' ||
    mutation.type === 'updateB2BMarketCartTotal' ||
    mutation.type === 'removeB2bGlobalProduct' ||
    mutation.type === 'removeUserProductFromGlobalCart' ||
    mutation.type === 'removeRecipientFromGlobalCart'
  ) {
    window.dispatchEvent(
      new CustomEvent('updated_b2b_carts', { detail: { store } })
    );
  }

  if (mutation.type === 'setAddedContacts') {
    window.dispatchEvent(
      new CustomEvent('update_b2b_user', { detail: { store } })
    );
  }
});

export default store;
